import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '@/components/common';
import { useAdminGreeting } from '@/hooks/useAdminGreeting';
import { useUserContext } from '@/contexts/UserContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import type { AdminTab } from '@/types/admin.types';
import type { BuilderState } from '@/types/survey.types';
import { SurveyBuilderView } from './SurveyBuilderView';
import { SurveysTab } from './tabs/SurveysTab';
import { TemplatesTab } from './tabs/TemplatesTab';
import { UsersTab } from './tabs/UsersTab';
import { RulesTab } from './tabs/RulesTab';
import { NotificationsTab } from './tabs/NotificationsTab';
import { SystemTab } from './tabs/SystemTab';
import { MonitoreoTab } from './tabs/MonitoreoTab';
import { JobsTab } from './tabs/JobsTab';
import { AnalyticsView } from '@/views/AnalyticsView';
import './AdminView.css';

const MAIN_TABS: Array<{ id: AdminTab; icon: string; labelKey: string; adminGlobalOnly?: boolean }> = [
  { id: 'surveys',       icon: 'fa-clipboard-list', labelKey: 'admin.surveys'       },
  { id: 'templates',     icon: 'fa-layer-group',    labelKey: 'admin.templates'     },
  { id: 'analytics',     icon: 'fa-chart-bar',      labelKey: 'admin.analyticsTab'  },
  { id: 'rules',         icon: 'fa-sliders-h',      labelKey: 'admin.rulesTab'      },
  { id: 'users',         icon: 'fa-users-cog',      labelKey: 'admin.users'         },
  { id: 'notifications', icon: 'fa-bell',           labelKey: 'admin.notifications' },
  { id: 'system',        icon: 'fa-heartbeat',      labelKey: 'admin.systemTab',   adminGlobalOnly: true },
  { id: 'monitor',       icon: 'fa-stream',         labelKey: 'admin.monitorTab',  adminGlobalOnly: true },
  { id: 'jobs',          icon: 'fa-sync',           labelKey: 'admin.jobsTab',     adminGlobalOnly: true },
];

export function AdminView() {
  const { t } = useTranslation();
  const greeting = useAdminGreeting();
  const { demoUser } = useUserContext();
  const currentUser = useCurrentUser();
  const activeRole = currentUser?.primaryRole ?? demoUser.role;
  const roleLabelKey = activeRole === 'AdminGlobal' ? 'admin.roleAdminCentral' : 'admin.roleAdminOU';
  const [activeTab, setActiveTab] = useState<AdminTab>(() => {
    try { return (localStorage.getItem('admin-activeTab') as AdminTab) ?? 'surveys'; }
    catch { return 'surveys'; }
  });
  const [builder, setBuilder] = useState<BuilderState | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('admin-sidebar-collapsed') === '1'; }
    catch { return false; }
  });
  const [pendingTab, setPendingTab] = useState<AdminTab | null>(null);

  useEffect(() => {
    try { localStorage.setItem('admin-activeTab', activeTab); } catch { /* storage unavailable */ }
  }, [activeTab]);

  function handleSidebarTab(tab: AdminTab) {
    if (builder) {
      setPendingTab(tab);
    } else {
      setActiveTab(tab);
    }
  }

  function confirmNavAway() {
    if (pendingTab) {
      setActiveTab(pendingTab);
      setBuilder(null);
      setPendingTab(null);
    }
  }

  function toggleCollapse() {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('admin-sidebar-collapsed', next ? '1' : '0'); }
      catch { /* storage unavailable */ }
      return next;
    });
  }

  return (
    <div className="admin-view">
      <div className="admin-container">
        {/* Sidebar */}
        <aside className={`admin-sidebar${collapsed ? ' collapsed' : ''}`} id="admin-sidebar">
          <div className="sidebar-header">
            <h3 className="sidebar-text">{t('admin.panelTitle')}</h3>
            <span className="admin-badge sidebar-text">{t(roleLabelKey)}</span>
            <p className="admin-greeting sidebar-text">{greeting}</p>
          </div>
          <nav className="sidebar-nav">
            {MAIN_TABS.filter(tab => !tab.adminGlobalOnly || activeRole === 'AdminGlobal').map(tab => (
              <button
                key={tab.id}
                className={`sidebar-link${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => handleSidebarTab(tab.id)}
                title={t(tab.labelKey)}
              >
                <i className={`fas ${tab.icon}`} aria-hidden="true" />
                <span className="sidebar-text"> {t(tab.labelKey)}</span>
              </button>
            ))}
          </nav>
          <button
            className="sidebar-collapse-btn"
            onClick={toggleCollapse}
            title={collapsed ? t('admin.sidebarExpand') : t('admin.sidebarCollapse')}
            aria-label={collapsed ? t('admin.sidebarExpand') : t('admin.sidebarCollapse')}
          >
            <i className="fas fa-chevron-left" aria-hidden="true" />
          </button>
        </aside>

        {/* Content */}
        <main
          className="admin-content"
          style={builder ? { padding: 0, overflow: 'hidden' } : undefined}
        >
          {builder ? (
            <SurveyBuilderView
              mode={builder.mode}
              surveyId={builder.surveyId}
              template={builder.template}
              source={builder.source}
              onClose={() => setBuilder(null)}
            />
          ) : (
            <>
              {activeTab === 'surveys'       && <SurveysTab onOpenBuilder={m => setBuilder(m)} />}
              {activeTab === 'templates'     && <TemplatesTab onOpenBuilder={state => setBuilder(state)} />}
              {activeTab === 'analytics'     && <AnalyticsView />}
              {activeTab === 'rules'         && <RulesTab />}
              {activeTab === 'users'         && <UsersTab />}
              {activeTab === 'notifications' && <NotificationsTab />}
              {activeTab === 'system'        && activeRole === 'AdminGlobal' && <SystemTab />}
              {activeTab === 'monitor'       && activeRole === 'AdminGlobal' && <MonitoreoTab />}
              {activeTab === 'jobs'          && activeRole === 'AdminGlobal' && <JobsTab />}
            </>
          )}
        </main>
      </div>

      {pendingTab && (
        <ConfirmModal
          title={t('admin.builderExitTitle')}
          message={t('admin.builderExitDesc')}
          confirmText={t('admin.builderExitConfirm')}
          cancelText={t('admin.builderExitCancel')}
          type="warning"
          onConfirm={confirmNavAway}
          onClose={() => setPendingTab(null)}
        />
      )}
    </div>
  );
}
